import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { api } from "../lib/api.js";
import type { ContentBlock, ContentFormat } from "../lib/types.js";
import { ContentContext, type ContentState } from "./contentState.js";

/**
 * Loads every copy override once, at boot.
 *
 * The whole table is a few dozen short strings, so one request beats a request
 * per paragraph — and fetching per block would show each default for a frame
 * before its override replaced it, which reads as the page flickering.
 *
 * A failed fetch is deliberately silent. Every block has its real copy written
 * into the page it belongs to, so an API that is down or unreachable renders
 * the site exactly as it shipped rather than a page full of error states.
 */
export function ContentProvider({ children }: { children: ReactNode }) {
  const [blocks, setBlocks] = useState<Record<string, ContentBlock>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    api
      .listContent(controller.signal)
      .then((response) => setBlocks(response.blocks))
      .catch(() => {
        // Defaults are already on screen; nothing to recover.
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, []);

  const save = useCallback(
    async (key: string, value: string, format: ContentFormat) => {
      const { block } = await api.updateContent(key, value, format);

      setBlocks((current) => {
        const next = { ...current };

        // A null block means the override was cleared, so the key has to leave
        // the map entirely — keeping it with an empty value would render a
        // blank where the page's own copy belongs.
        if (block) next[key] = block;
        else delete next[key];

        return next;
      });
    },
    [],
  );

  const value = useMemo<ContentState>(
    () => ({ blocks, loading, save }),
    [blocks, loading, save],
  );

  return (
    <ContentContext.Provider value={value}>{children}</ContentContext.Provider>
  );
}
